import { StateMachine } from "@/core/arch/state_machine";
import { groupQuestions } from "./grouping";
import type { AssistantRepository } from "./assistant_repository";
import type {
  AssistantTurnRow,
  FailureFilter,
  GapDisposition,
  QuestionGroup,
} from "./types";
import { gapDispositionCopy, RETENTION_DAYS } from "./types";

/** Selectable look-back windows. Nothing beyond retention, which holds nothing. */
export const WINDOW_OPTIONS = [7, 30, RETENTION_DAYS] as const;

export type WindowDays = (typeof WINDOW_OPTIONS)[number];

export const DEFAULT_DISPOSITION: GapDisposition = "out_of_scope";
export const DEFAULT_WINDOW: WindowDays = 30;

export interface AssistantGapsState {
  status: "idle" | "loading" | "loaded" | "error";
  disposition: GapDisposition;
  windowDays: WindowDays;
  groups: QuestionGroup[];
  /** Turns in this view, after filtering and before grouping. */
  turns: number;
  /**
   * Turns the read returned, before this view's filter.
   *
   * Separate from `turns` because the truncation warning is about the read,
   * not about the view: ten failures found among two thousand rows would
   * otherwise announce that the window covers only the ten most recent turns.
   */
  fetched: number;
  /** The read hit its ceiling, so every count below is a floor. */
  truncated: boolean;
  error?: string;
}

const INITIAL: AssistantGapsState = {
  status: "idle",
  disposition: DEFAULT_DISPOSITION,
  windowDays: DEFAULT_WINDOW,
  groups: [],
  turns: 0,
  fetched: 0,
  truncated: false,
};

/**
 * Whether a turn belongs to this view.
 *
 * `out_of_scope` is two populations wearing one name: the model choosing it
 * because nothing in the reference bore on the question, and `runTurn`
 * falling back to it after both drafts failed. Only the second carries a
 * `groundFailure`, so the column is the whole test — and without it, a defect
 * reads as missing documentation.
 */
function matches(filter: FailureFilter, row: AssistantTurnRow): boolean {
  if (filter === "all") return true;
  const failed =
    row.groundFailure !== null &&
    row.groundFailure !== undefined &&
    row.groundFailure.trim() !== "";
  return filter === "only" ? failed : !failed;
}

/**
 * Backs the assistant gaps page.
 *
 * Grouping and ranking happen here rather than server-side because the ranking
 * question — how many distinct merchants asked this — cannot be answered by a
 * DynamoDB query, and the read is bounded, so the whole result fits in memory.
 */
export class AssistantGapsBloc extends StateMachine<AssistantGapsState> {
  /** Only the newest in-flight load may commit, so a slow one cannot win. */
  private loadToken = 0;

  constructor(private readonly repo: AssistantRepository) {
    super(INITIAL);
  }

  async load(): Promise<void> {
    const token = ++this.loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const view = gapDispositionCopy(this.state.disposition);
      const { rows, truncated } = await this.repo.listTurns({
        disposition: view.disposition,
        windowDays: this.state.windowDays,
      });
      const kept = rows.filter((row) => matches(view.failures, row));
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this.loadToken) return;

      this.setState({
        ...this.state,
        status: "loaded",
        groups: groupQuestions(kept),
        turns: kept.length,
        fetched: rows.length,
        truncated,
        error: undefined,
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this.loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        groups: [],
        turns: 0,
        fetched: 0,
        truncated: false,
        error: err instanceof Error ? err.message : "Failed to load turns",
      });
    }
  }

  async setDisposition(disposition: GapDisposition): Promise<void> {
    if (disposition === this.state.disposition) return;
    this.setState({
      ...this.state,
      disposition,
      groups: [],
      turns: 0,
      fetched: 0,
    });
    await this.load();
  }

  async setWindow(windowDays: WindowDays): Promise<void> {
    if (windowDays === this.state.windowDays) return;
    this.setState({
      ...this.state,
      windowDays,
      groups: [],
      turns: 0,
      fetched: 0,
    });
    await this.load();
  }
}
